import { bootstrapApplication } from "@angular/platform-browser";
import { provideRouter } from "@angular/router";
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { uploadProgressInterceptor } from "./app/shared/http/upload-progress.interceptor";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideNgxWebstorage, withLocalStorage, withSessionStorage, withNgxWebstorageConfig } from "ngx-webstorage";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { HttpClient } from "@angular/common/http";
import { HybridTranslateLoader } from "./app/shared/loaders/hybrid-translate-loader";
import Aura from "@primeuix/themes/aura";
import { definePreset } from "@primeuix/themes";
import { providePrimeNG } from "primeng/config";

// Custom YLLW preset based on Aura with black as primary color
const YllwPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '#f5f5f5',
            100: '#e0e0e0',
            200: '#bdbdbd',
            300: '#9e9e9e',
            400: '#757575',
            500: '#000000',
            600: '#000000',
            700: '#000000',
            800: '#000000',
            900: '#000000',
            950: '#000000'
        }
    }
});
import { MessageService, ConfirmationService } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { importProvidersFrom, LOCALE_ID, ErrorHandler, provideZoneChangeDetection, APP_INITIALIZER } from "@angular/core";
import { registerLocaleData } from "@angular/common";
import localeSv from "@angular/common/locales/sv";

// App imports
import { AppComponent } from "./app/application/app.component";
import { routes } from "./app/routing/application/application.routes";
import { API_BASE_URL, ContractReminderClient } from "./api-service";
import { environment } from "@env/environment";
import { AppStoreModule } from "./app/store/app-store.module";
import { TitleCasePipe, CurrencyPipe, DatePipe } from "@angular/common";
import { CoreCompanyDatePipe } from "./app/shared/pipe/core-company-date.pipe";

// API Client imports
import {
    QuoteClient, SalesPersonClient, CustomerClient, DeliveryTermsClient, ItemCategoryClient,
    LocationClient, FinancingCompaniesClient, PaymentTermsClient, VersionClient,
    CurrencyClient, AdminClient, DeliveryAddressClient, SalesSupportPersonClient, QuoteItemClient,
    BrandClient, AttioClient,
    ContractClient, ContractFileClient, ContractSectionClient, VendorInquiryClient, UserClient, PdfClient, ScheduledJobsClient,
    CreditCheckClient, SubscriptionQuoteClient, InterestRateClient,
    CatalogClient, CatalogProductClient, EcoLabelClient, UnspscClient,
    MarketingAutomationClient, NotificationClient, DrawingClient, CompanyClient,
    ExternalUserManagementClient, CountryClient, CountryProjectManagementClient, ContractDiscountClient,
    QuoteSalesCreditClient, SalesCreditRoleClient, SalesOrderClient, TermsClient, TranslationClient,
    ContactClient, CustomerTypeClient
} from "./api-service";

// MSAL imports
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService,
} from "@azure/msal-angular";
import {
    BrowserCacheLocation,
    InteractionType,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication
} from "@azure/msal-browser";
import { HTTP_INTERCEPTORS } from "@angular/common/http";

// Factory functions
export function loggerCallback(logLevel: LogLevel, message: string) {
    console.log(logLevel, message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: environment.ssoClientId,
            authority: "https://login.microsoftonline.com/organizations",
            redirectUri: "/",
            postLogoutRedirectUri: "/",
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
        },
        system: {
            loggerOptions: {
                loggerCallback,
                logLevel: LogLevel.Error,
                piiLoggingEnabled: false,
            },
        },
    });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string> | null>();

    // Anonymous endpoints - exclude from MSAL authentication (token-based PDF system)
    // Note: Use exact path without wildcard since these endpoints use query params, not path segments
    protectedResourceMap.set(`${environment.apiUrl}/pdf/view/quote`, null);
    protectedResourceMap.set(`${environment.apiUrl}/pdf/view/subscription`, null);
    protectedResourceMap.set(`${environment.apiUrl}/pdf/view/vendor-inquiry`, null);

    // All other API endpoints require authentication
    protectedResourceMap.set(`${environment.apiUrl}/*`, environment.scopes.read);

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [...environment.scopes.read, ...environment.scopes.write],
        },
        loginFailedRoute: "/login-failed",
    };
}

// MSAL Initialization - ensures MSAL is fully ready before app starts
export function MSALInitializerFactory(msalInstance: IPublicClientApplication): () => Promise<void> {
    return async () => {
        await msalInstance.initialize();
        await msalInstance.handleRedirectPromise();
    };
}

// Register locale data
registerLocaleData(localeSv);

bootstrapApplication(AppComponent, {
    providers: [
        // Router
        provideZoneChangeDetection(), provideRouter(routes),

        // HTTP Client
        provideHttpClient(withInterceptorsFromDi(), withInterceptors([uploadProgressInterceptor])),

        // Animations
        provideAnimationsAsync(),

        // PrimeNG Theme Configuration
        providePrimeNG({
            theme: {
                preset: YllwPreset,
                options: {
                    darkModeSelector: false, // Disable dark mode
                    cssLayer: false // Don't use CSS layers for easier overrides
                }
            }
        }),

        // PrimeNG Global Services
        MessageService,
        ConfirmationService,
        DialogService,

        // Locale
        { provide: LOCALE_ID, useValue: "sv" },

        // NGX Webstorage
        provideNgxWebstorage(
            withNgxWebstorageConfig({ separator: ":", caseSensitive: true }),
            withLocalStorage(),
            withSessionStorage()
        ),

        // Translation (HybridTranslateLoader: JSON + DB overrides)
        importProvidersFrom(TranslateModule.forRoot({
            fallbackLang: "sv",
            loader: {
                provide: TranslateLoader,
                useFactory: (http: HttpClient, translationClient: TranslationClient) => new HybridTranslateLoader(http, translationClient),
                deps: [HttpClient, TranslationClient],
            },
        })),

        // Store
        importProvidersFrom(AppStoreModule),

        // Shared pipes (for injection)
        TitleCasePipe,
        CurrencyPipe,
        DatePipe,
        CoreCompanyDatePipe,

        // API Client Services
        SalesPersonClient,
        QuoteClient,
        CustomerClient,
        DeliveryTermsClient,
        ItemCategoryClient,
        LocationClient,
        FinancingCompaniesClient,
        PaymentTermsClient,
        VersionClient,
        CurrencyClient,
        InterestRateClient,
        AdminClient,
        DeliveryAddressClient,
        SalesSupportPersonClient,
        BrandClient,
        QuoteItemClient,
        AttioClient,
        ContractClient,
        ContractFileClient,
        ContractSectionClient,
        VendorInquiryClient,
        UserClient,
        PdfClient,
        ScheduledJobsClient,
        CreditCheckClient,
        SubscriptionQuoteClient,
        QuoteSalesCreditClient,
        SalesCreditRoleClient,
        CatalogClient,
        CatalogProductClient,
        EcoLabelClient,
        UnspscClient,
        MarketingAutomationClient,
        DrawingClient,
        NotificationClient,
        CompanyClient,
        ExternalUserManagementClient,
        CountryClient,
        CountryProjectManagementClient,
        ContractReminderClient,
        ContractDiscountClient,
        SalesOrderClient,
        TermsClient,
        TranslationClient,
        ContactClient,
        CustomerTypeClient,

        // Error Handler
        { provide: ErrorHandler },

        // MSAL Services
        MsalService,
        MsalGuard,
        MsalBroadcastService,

        // API Base URL
        {
            provide: API_BASE_URL,
            useValue: environment.apiUrl,
        },

        // HTTP Interceptors
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },

        // MSAL Configuration
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },

        // MSAL Initialization - must run before app starts
        {
            provide: APP_INITIALIZER,
            useFactory: MSALInitializerFactory,
            deps: [MSAL_INSTANCE],
            multi: true,
        },
    ]
}).catch(err => console.error(err));
